import {Trans} from '@lingui/macro';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {AsyncHandlerOf, Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {TextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {emojiToUnified} from '../../../../../srcCommon/helpers/emoji/emojiHelpers';
import {EmojiDefinitionAndSkinTone} from '../../../../../srcCommon/helpers/emoji/emojiTextHelpers';
import {computeHighlightPickerValues} from '../../../../helpers/models/highlightHelpers';
import {HighlightTypesEnum} from '../../../../models/api/highlightBaseModel';
import {InboxModel} from '../../../../models/api/inboxModel';
import {PermissionsEnum} from '../../../../models/api/roleModel';
import {TagModel} from '../../../../models/api/tagModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {LensTemplate} from '../../../../store/data/lenses/saveLensActions';
import {PaletteColorPicker} from '../../../common/color/paletteColorPicker';
import {OkCancelDialog} from '../../../common/dialogs/okCancelDialog';
import {EmojiSelectHandler} from '../../../common/dropdowns/emoji/emojiDropdown';
import {NamespaceDropdownBoxContainer} from '../../../common/dropdowns/namespace/namespaceDropdownBoxContainer';
import {TagEditorEmoji} from '../../../common/dropdowns/tags/tagEditorEmoji';
import {LensFormMultiSelectAssigneesContainer} from '../../../lenses/form/lensFormMultiSelectAssigneesContainer';
import {LensFormMultiSelectInboxesContainer} from '../../../lenses/form/lensFormMultiSelectInboxesContainer';
import {LensFormMultiSelectTags} from '../../../lenses/form/lensFormMultiSelectTags';

/*
 * Props.
 */

interface LensesWorkspaceCreateDialogProps {
  onSubmit: AsyncHandlerOf<LensTemplate>;
  onRequestClose: Handler;
  userNamespace: string;
}

/*
 * Styles.
 */

// TODO-LENS: These styled components were stolen from TagEditorBody but need more work.

const StyledFieldDiv = styled.div`
  & + & {
    margin-top: 9px;
  }
`;

const StyledSectionDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  grid-template-areas: 'title toggle';
  align-items: center;
`;

const StyledTitleDiv = styled.div`
  grid-area: title;
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledHighlightDiv = styled.div`
  margin: 15px 0 18px;

  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: min-content;
  grid-column-gap: 5px;
  align-items: center;
  justify-content: center;
`;

/*
 * Component.
 */

export const LensesWorkspacesCreateDialog: FC<LensesWorkspaceCreateDialogProps> = ({
  onSubmit,
  onRequestClose,
  userNamespace,
}) => {
  // TODO-LENS: add error state -- likely needs to be prop from container instead.
  // const [formError, setFormError] = useState<CustomError | null>(null); // may need to be prop from container instead
  const formError = null;

  const [name, setName] = useState('');
  const [selectedNamespace, setSelectedNamespace] = useState<string | undefined>(undefined);
  const [selectedInboxIds, setSelectedInboxIds] = useState<ReadonlyArray<InboxModel['id']>>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<ReadonlyArray<TagModel['id']>>([]);
  const [selectedTeammateIds, setSelectedTeammateIds] = useState<ReadonlyArray<TeammateModel['id']>>([]);

  const onNamespaceSelect: HandlerOf<string> = (newNamespace) => {
    // Update the selected namespace.
    setSelectedNamespace(newNamespace);

    // Also clear all ingredient fields. (Consider useReducer instead for tracking these dependent state updates)
    setSelectedInboxIds([]);
    setSelectedTagIds([]);
    // While we don't have to clear assignees because the choices are all company teammate, not all team members,
    //  it seems sensible to clear all ingredient fields when changing the team.
    setSelectedTeammateIds([]);
  };

  const [highlightColor, setHighlightColor] = useState<PaletteColorsEnum | undefined>(undefined);
  const [highlightEmoji, setHighlightEmoji] = useState<EmojiDefinitionAndSkinTone | undefined>(undefined);
  const displayValues = computeHighlightPickerValues(highlightColor, highlightEmoji);

  const onEmojiSelect: EmojiSelectHandler = (_shortcode, definition) => {
    setHighlightColor(undefined);
    setHighlightEmoji(definition);
  };

  const onColorSelect: HandlerOf<PaletteColorsEnum | undefined> = (paletteColor) => {
    setHighlightColor(paletteColor);
    setHighlightEmoji(undefined);
  };

  /** Returns null if the form is in an invalid state. Otherwise, returns the template. */
  const buildValidTemplate = (): LensTemplate | null => {
    if (!name || !selectedNamespace || !selectedInboxIds.length) {
      return null;
    }

    const baseProps = {
      name,
      namespace: userNamespace,
      tagIds: selectedTagIds,
      assigneeTeammateIds: selectedTeammateIds,
      inboxIds: selectedInboxIds,
      highlight: highlightEmoji || highlightColor,
    };

    if (highlightColor) {
      return {
        ...baseProps,
        highlightColor,
        highlightEmoji: undefined,
        highlightType: HighlightTypesEnum.COLOR,
      };
    }

    if (highlightEmoji) {
      return {
        ...baseProps,
        highlightColor: undefined,
        highlightEmoji: emojiToUnified(highlightEmoji.emoji, highlightEmoji.skinTone),
        highlightType: HighlightTypesEnum.EMOJI,
      };
    }

    return {
      ...baseProps,
      highlightType: HighlightTypesEnum.NONE,
      highlightColor: undefined,
      highlightEmoji: undefined,
    };
  };

  const onOkClick = async () => {
    const template = buildValidTemplate();
    if (!template) {
      // TODO-LENS: better validation.
      alert('invalid!');
      return;
    }

    try {
      await onSubmit(template);
    } catch (error) {
      // TODO-LENS: better error handling.
      alert(`Error: ${error}`);
      return;
    }

    onRequestClose();
  };

  return (
    <OkCancelDialog
      okButtonLabel={<Trans id="lenses-workspace-create-dialog-create">Create</Trans>}
      title={<Trans id="lenses-workspace-create-dialog-title">Create view</Trans>}
      onOkClick={onOkClick}
      onRequestClose={onRequestClose}
    >
      {/* Name input. */}
      <StyledFieldDiv>
        <TextBox
          label={<Trans id="lenses-workspace-create-dialog-label-name">Name</Trans>}
          value={name}
          onChange={setName}
          hasError={Boolean(formError)}
          // TODO-LENS: error state.
          // errorMessage={<TagEditorCreationError error={tagError} />}
          shouldAutoFocus
          isRequired
          isDisabled={false}
        />
      </StyledFieldDiv>

      {/* Highlight (color/emoji) input. */}
      <StyledFieldDiv>
        <StyledSectionDiv>
          <StyledTitleDiv>
            <Trans id="lenses-workspace-create-dialog-label-highlight">Color or emoji</Trans>
          </StyledTitleDiv>
        </StyledSectionDiv>
        <StyledHighlightDiv>
          <PaletteColorPicker paletteColor={displayValues.color} onColorSelect={onColorSelect} />
          <TagEditorEmoji
            emoji={displayValues.emoji.emoji}
            skinTone={displayValues.emoji.skinTone}
            onEmojiSelect={onEmojiSelect}
            isSelected={Boolean(highlightEmoji)}
          />
        </StyledHighlightDiv>
      </StyledFieldDiv>

      {/* Team picker. */}
      <StyledFieldDiv>
        <NamespaceDropdownBoxContainer
          selectedNamespace={selectedNamespace}
          onNamespaceSelect={onNamespaceSelect}
          label={
            <Trans id="lenses-workspace-create-dialog-label-team">Show conversations from this team</Trans>
          }
          placeholder={<Trans id="lenses-workspace-create-dialog-placeholder-team">Select team</Trans>}
          // By setting resourceNamespace to the userNamespace,
          //  this dropdown will include all teams the user has the given permission for.
          resourceNamespace={userNamespace}
          permission={PermissionsEnum.READ_INBOX}
          // TODO-LENS: may become true when we add editing flow w/ isEditing.
          isDisplayOnly={false}
          isRequired
          shouldHideUserNamespaceChoice
        />
      </StyledFieldDiv>

      {/* Inboxes picker. */}
      <StyledFieldDiv>
        <LensFormMultiSelectInboxesContainer
          namespace={selectedNamespace}
          selectedIds={selectedInboxIds}
          onSetItemIds={setSelectedInboxIds}
          isDisabled={!selectedNamespace}
        />
      </StyledFieldDiv>

      {/* Tags picker. */}
      <StyledFieldDiv>
        <LensFormMultiSelectTags
          namespace={selectedNamespace}
          selectedIds={selectedTagIds}
          onSetItemIds={setSelectedTagIds}
          isDisabled={!selectedNamespace}
        />
      </StyledFieldDiv>

      {/* Assignees picker. */}
      <StyledFieldDiv>
        <LensFormMultiSelectAssigneesContainer
          selectedIds={selectedTeammateIds}
          onSetItemIds={setSelectedTeammateIds}
          isDisabled={!selectedNamespace}
        />
      </StyledFieldDiv>
    </OkCancelDialog>
  );
};
